/**
 * Surface configuration doorway (TRF port of cpv3 #1302 arch doc §1/§3;
 * chat/voice parity #1731, spec §4.1).
 *
 * Upstream, the core never imports brand code by path — the ONE sanctioned
 * import is the abstract specifier '@brand/chat-config', which each brand’s
 * build resolves to its own static config module. TRF ships ONE bundle for
 * TWO audiences (the family companion surface and the TRFer Williams
 * surface), so the config cannot be static per build. It is delivered at
 * runtime instead:
 *
 *  - `ChatSurfaceProvider` installs a `ChatSurfaceConfig` for the tree it
 *    wraps. Components read it with `useChatSurface()`, which throws outside
 *    a provider (a surface rendered with no audience is a bug, not a default).
 *  - `chatConfig()` / `chatCopy()` keep their upstream shape so the ~20 core
 *    call sites — including non-React service modules, which cannot use a
 *    hook — stay byte-identical for cherry-porting. They read the config the
 *    nearest mounted provider installed, and fall back to the doorway’s
 *    default (the companion audience) when none is mounted, which is what
 *    the ported unit suites rely on exactly as they relied on the static
 *    import upstream.
 *
 * '@chat-config' (src/chat-config/index.ts) is still the ONE sanctioned
 * crossing from the core into audience land; Guard B’s lint half allows
 * precisely this specifier and '@core/*' — see eslint.config.js.
 */
import { createContext, createElement, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { defaultChatConfig } from '@chat-config'
import type { BrandChatConfig, ChatCopyPack } from './types'

export type * from './types'

/** The runtime-delivered config type; identical to upstream’s BrandChatConfig. */
export type ChatSurfaceConfig = BrandChatConfig

const ChatSurfaceContext = createContext<ChatSurfaceConfig | null>(null)

/**
 * The config the most recently rendered provider installed, read by the
 * plain accessors below. Set during render (not in an effect) so children of
 * the provider see it in the SAME render pass — ThinkingIndicator and the
 * drawer call chatCopy() while rendering.
 */
let activeConfig: ChatSurfaceConfig | null = null

export function ChatSurfaceProvider(props: { config: ChatSurfaceConfig; children?: ReactNode }) {
  const { config, children } = props
  useMemo(() => {
    activeConfig = config
  }, [config])
  useEffect(() => {
    activeConfig = config
    return () => {
      if (activeConfig === config) activeConfig = null
    }
  }, [config])
  return createElement(ChatSurfaceContext.Provider, { value: config }, children)
}

/** The active surface config. Throws outside a ChatSurfaceProvider. */
export function useChatSurface(): ChatSurfaceConfig {
  const value = useContext(ChatSurfaceContext)
  if (!value) {
    throw new Error(
      '[chat-core] useChatSurface() was called outside a ChatSurfaceProvider — wrap the surface in one and pass resolveChatConfig(audience).',
    )
  }
  return value
}

/**
 * #2109 R3-B24: the copy pack for a component that must re-render when the
 * surface's config changes — a language switch hands the provider a new
 * config. Reading the context subscribes the caller, which a plain
 * `chatCopy()` call cannot do, and it reaches through `React.memo`
 * comparators (ChatBubble, MessageList) that would otherwise keep the old
 * language. Unlike `useChatSurface()` it does not throw outside a provider:
 * it falls back to the same default the plain accessor reads.
 */
export function useChatCopy(): ChatCopyPack {
  const value = useContext(ChatSurfaceContext)
  return (value ?? chatConfig()).copy
}

export function chatConfig(): BrandChatConfig {
  return activeConfig ?? defaultChatConfig
}

export function chatCopy(): ChatCopyPack {
  return chatConfig().copy
}
